/**
 * Single decision point for "does this merchant see the appointment-limit
 * upsell, and with what numbers".
 *
 * Both consumers go through here so they cannot disagree:
 *   - `getAppSettings`, which hands the result to the micro-apps as a flag
 *   - `BookingsUpgradeContext`, which drives the side-menu card and the dialog
 */
import { WSB_UPGRADE_URL } from '../../constants';
import { WSB_MERCHANT_SOURCE } from '../../shared/constants/merchant';
import type { AppointmentUsage } from './usage';

/** What a surface needs to render the upsell. */
export interface UpgradeEligibility extends AppointmentUsage {
  /**
   * One.com's WSB subscription page, taken as-is from `WSB_UPGRADE_URL` (see
   * environments.js). No locale path and no query parameters: the merchant is
   * already signed in to One.com — app-shell runs inside its iframe — so the
   * page resolves their project and plan itself. `null` on environments that
   * configure none.
   */
  upgradeUrl: string | null;
}

/** The merchant fields the decision depends on. */
export interface EligibilityInput {
  source: string;
  /** `null` for merchants with no allowance. */
  appointmentUsage: AppointmentUsage | null;
}

/**
 * Resolves the upsell for a merchant, or `null` when it does not apply.
 *
 * `null` is the "uncapped" case: non-WSB merchants, the beta toggle being off,
 * and merchants whose allowance could not be resolved. Core fails open in the
 * same situations — where it would not block, we do not warn.
 *
 * @param merchant - Merchant fields from the `currentMerchant` slice.
 * @param isBetaEnabled - Whether the `/beta` toggle is on. Passed in rather than
 *   read here so React callers can supply a value from `useLocalStorageState`
 *   and re-render when the switch flips.
 */
export function resolveUpgradeEligibility(
  merchant: EligibilityInput,
  isBetaEnabled: boolean
): UpgradeEligibility | null {
  if (!isBetaEnabled) return null;

  const { source, appointmentUsage: usage } = merchant;

  if (source !== WSB_MERCHANT_SOURCE || !usage || usage.limit <= 0) return null;

  return {
    ...usage,
    upgradeUrl: WSB_UPGRADE_URL || null,
  };
}
